import type { CSSProperties, ReactNode } from 'react';

export type BadgeTone =
  | 'neutral'
  | 'ok'
  | 'warn'
  | 'risk'
  | 'info'
  | 'accent'
  | 'outline';

export type BadgeSize = 'sm' | 'md' | 'lg';

const TONES: Record<BadgeTone, { bg: string; fg: string; dot: string }> = {
  neutral: { bg: 'var(--apm-bg-sunken)', fg: 'var(--apm-ink-2)', dot: 'var(--apm-muted)' },
  ok:      { bg: 'var(--apm-ok-soft)', fg: 'var(--apm-ok)', dot: 'var(--apm-ok)' },
  warn:    { bg: 'var(--apm-warn-soft)', fg: 'var(--apm-warn)', dot: 'var(--apm-warn)' },
  risk:    { bg: 'var(--apm-risk-soft)', fg: 'var(--apm-risk)', dot: 'var(--apm-risk)' },
  info:    { bg: 'var(--apm-info-soft)', fg: 'var(--apm-info)', dot: 'var(--apm-info)' },
  accent:  { bg: 'var(--apm-accent-soft)', fg: 'var(--apm-accent-ink)', dot: 'var(--apm-accent)' },
  outline: { bg: 'transparent', fg: 'var(--apm-muted)', dot: 'var(--apm-muted)' },
};

const SIZES: Record<BadgeSize, { h: number; px: number; fs: number }> = {
  sm: { h: 20, px: 6, fs: 11 },
  md: { h: 24, px: 8, fs: 12 },
  lg: { h: 28, px: 10, fs: 13 },
};

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
  icon?: ReactNode;
  dot?: boolean;
  style?: CSSProperties;
}

export function Badge({
  children,
  tone = 'neutral',
  size = 'md',
  icon,
  dot,
  style,
}: BadgeProps) {
  const t = TONES[tone];
  const s = SIZES[size];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: s.h,
        padding: `0 ${s.px}px`,
        background: t.bg,
        color: t.fg,
        border: tone === 'outline' ? '1px solid var(--apm-line-2)' : 'none',
        borderRadius: 999,
        fontSize: s.fs,
        fontWeight: 500,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: t.dot,
          }}
        />
      )}
      {icon}
      {children}
    </span>
  );
}
